import axios from "axios"
import { NextResponse } from "next/server"
import { env } from "@/env"
import { getSession } from "@/services/auth-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

// Regex patterns to extract the Customer Reviews upload nonce from product page HTML
// The plugin embeds this in the review form as a data attribute or hidden field
const NONCE_PATTERNS = [
  // Pattern 1: data-nonce="abc123"
  /data-nonce=["']([^"']+)["']/,
  // Pattern 2: <input name="cr_nonce" value="abc123">
  /name=["']cr[_-]?nonce["'][^>]*value=["']([^"']+)["']/,
  // Pattern 3: "cr_nonce":"abc123"
  /"cr[_-]?nonce"\s*:\s*"([^"]+)"/,
  // Pattern 4: wp_create_nonce output in script
  /var\s+cr[_-]?nonce\s*=\s*["']([^"']+)["']/,
]

function extractNonce(html: string): string | null {
  for (const pattern of NONCE_PATTERNS) {
    const match = html.match(pattern)
    if (match?.[1]) {
      return match[1]
    }
  }
  return null
}

export async function GET(request: Request) {
  const session = await getSession()
  const sessionCookie = session?.wordpressSessionCookie?.trim()

  if (!(sessionCookie && session?.user)) {
    return NextResponse.json(
      {
        ok: false,
        code: "UNAUTHORIZED",
        message: "Authentication required",
      },
      {
        status: 401,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  // Get productId from query params
  const url = new URL(request.url)
  const productId = url.searchParams.get("productId")

  if (!productId) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_REQUEST",
        message: "Product ID is required",
      },
      {
        status: 400,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  try {
    console.log("[Nonce Route] Fetching product page to extract nonce...")
    console.log("[Nonce Route] Product ID:", productId)

    // Fetch the product page HTML where the review form (with nonce) is embedded
    const wpBaseUrl = env.WP_API_URL.replace(/\/wp-json\/?$/, "")
    const productPageUrl = `${wpBaseUrl}/?p=${productId}`

    const response = await axios.get<string>(productPageUrl, {
      headers: {
        Cookie: sessionCookie,
      },
    })

    const html = typeof response.data === "string" ? response.data : ""
    const nonce = extractNonce(html)

    if (!nonce) {
      console.error("[Nonce Route] Nonce not found in product page HTML")
      console.error("[Nonce Route] Tried product URL:", productPageUrl)

      return NextResponse.json(
        {
          ok: false,
          code: "NONCE_NOT_FOUND",
          message:
            "Upload nonce not found in product page. Make sure you have permission to review this product.",
        },
        {
          status: 502,
          headers: NO_CACHE_HEADERS,
        }
      )
    }

    console.log("[Nonce Route] Nonce extracted successfully")
    return NextResponse.json(
      {
        ok: true,
        nonce,
      },
      {
        headers: NO_CACHE_HEADERS,
      }
    )
  } catch (error) {
    console.error("[Nonce Route] Error fetching nonce:", error)
    return NextResponse.json(
      {
        ok: false,
        code: "NONCE_FETCH_FAILED",
        message: "Failed to fetch upload nonce",
        details: error instanceof Error ? error.message : "Unknown error",
      },
      {
        status: 502,
        headers: NO_CACHE_HEADERS,
      }
    )
  }
}
